
import PieChart from "components/charts/PieChart";
import { pieChartData, pieChartOptions } from "variables/charts";
import Card from "components/card";
import React, { useState, useRef, useEffect } from "react";
import { MdRefresh } from "react-icons/md";
import { getToken } from "utils/auth";

const PieChartCard = ({ loading, pieData, onDataRefreshed }) => {
  const [period, setPeriod] = useState("month");
  const [localPieData, setLocalPieData] = useState(pieData);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const refreshTimeoutRef = useRef(null);

  // Handle refresh button click
  const handleRefresh = async () => {
    if (isRefreshing) return; // Prevent multiple simultaneous requests

    setIsRefreshing(true);
    setRefreshError(null);

    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:3002";
      const token = getToken();
      const response = await fetch(`${baseUrl}/dashboard`, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error("Failed to refresh dashboard data");
      }

      const result = await response.json();
      const freshData = result.data;

      // Update local state with fresh data
      setLocalPieData(freshData?.leadSourceChart);

      // Call parent callback if provided
      if (onDataRefreshed) {
        onDataRefreshed(freshData);
      }

      // Show success toast
      if (window.__showToast) {
        window.__showToast("Lead Source refreshed successfully", "success");
      }
    } catch (err) {
      setRefreshError(err.message);
      // Show error toast
      if (window.__showToast) {
        window.__showToast(
          `Failed to refresh data: ${err.message}`,
          "error"
        );
      }
    } finally {
      setIsRefreshing(false);
      // Clear error after 5 seconds
      refreshTimeoutRef.current = setTimeout(() => {
        setRefreshError(null);
      }, 5000);
    }
  };

  // Sync local state with props when they change
  useEffect(() => {
    if (!isRefreshing) {
      setLocalPieData(pieData);
    }
  }, [pieData, isRefreshing]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Map period to API keys
  const periodMap = {
    month: "currentMonth",
    week: "currentWeek",
    year: "currentYear",
  };
  const periodLabel = {
    month: "Monthly",
    week: "Weekly",
    year: "Yearly",
  };

  // Get data for selected period
  const selected = localPieData?.[periodMap[period]];
  const labels = selected?.labels ? Object.keys(selected.labels) : pieChartOptions.labels;
  const values = selected?.labels ? Object.values(selected.labels) : pieChartData;
  const total = values.reduce((a, b) => a + b, 0);

  const pieChartColors = [
    "#fcb903",
    "#8585EF",
    "#61B9DE",
    "#f3c47e",
    "#82C8E5",
    "#3636c5",
    "#e18c14",
    "#8585EF",
    "#363BC9",
    "#292CD6",
  ];

  const chartColors = pieChartColors.slice(0, Math.min(labels.length, pieChartColors.length));

  // Pie chart options with dynamic labels/colors
  const chartOptions = {
    ...pieChartOptions,
    labels,
    colors: chartColors,
    fill: {
      ...pieChartOptions.fill,
      colors: chartColors,
    },
    dataLabels: {
      ...pieChartOptions.dataLabels,
      background: {
        enabled: false,
      },
    },
    plotOptions: {
      ...pieChartOptions.plotOptions,
      pie: {
        ...(pieChartOptions.plotOptions?.pie || {}),
        dataLabels: {
          ...(pieChartOptions.plotOptions?.pie?.dataLabels || {}),
          background: {
            enabled: false,
          },
        },
      },
    },
  };

  return (
    <>
      <style>{`
        @keyframes refresh-spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .refresh-icon-spin {
          animation: refresh-spin 1s linear infinite;
        }
      `}</style>
      <Card extra="rounded-[20px] p-3">
        <div className="flex flex-row justify-between px-3 pt-2">
          <div>
            <h4 className="text-lg font-bold text-navy-700 dark:text-white">
              Lead Source ({periodLabel[period]})
            </h4>
          </div>

          <div className="mb-6 flex items-center justify-center gap-2">
            <select
              className="mb-3 flex items-center justify-center text-sm font-bold text-gray-600 hover:cursor-pointer dark:!bg-navy-800 dark:text-white"
              value={period}
              onChange={e => setPeriod(e.target.value)}
            >
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
              <option value="week">Weekly</option>
            </select>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`!linear flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 !transition !duration-200 ${isRefreshing
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10 hover:cursor-pointer"
                }`}
              title={isRefreshing ? "Refreshing data..." : "Refresh Lead Source"}
            >
              <MdRefresh
                className={`h-5 w-5 ${isRefreshing ? "refresh-icon-spin" : ""}`}
              />
            </button>
          </div>
        </div>

        <div className="mb-auto flex h-[220px] w-full items-center justify-center">
          <PieChart options={chartOptions} series={values} />
        </div>
        <div className="flex flex-row flex-wrap !justify-between rounded-2xl px-6 py-3 shadow-2xl shadow-shadow-500 dark:!bg-navy-700 dark:shadow-none">
          {labels.map((label, idx) => (
            <div key={label} className="flex flex-col items-center justify-center mb-2">
              <div className="flex items-center justify-center">
                <div className="h-2 w-2 rounded-full" style={{ background: chartOptions.colors[idx] || "#4318FF" }} />
                <p className="ml-1 text-sm font-normal text-gray-600">{label}</p>
              </div>
              <p className="mt-px text-xl font-bold text-navy-700 dark:text-white">
                {total > 0 ? Math.round((values[idx] / total) * 100) : 0}%
              </p>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
};

export default PieChartCard;
